<template>
  <div>
    <b-row>
      <b-col
        v-for="chart in charts"
        :key="chart.key"
        cols="12"
        md="6"
        lg="4"
        class="mb-1-5 row-height"
      >
        <b-card
          class="header-card row-height h-100 w-100"
          :title="chart.title"
          body-class="d-flex flex-column align-items-start"
        >
          <template v-if="chart.hasData">
            <e-chart
              v-if="chart.type === 'chart'"
              :options="chart.option"
              autoresize
              class="h-100 w-100"
            />
            <b-table
              v-else-if="chart.type === 'table'"
              :items="chart.items"
              :fields="chart.fields"
              sort-by="time"
              :sort-desc="true"
              small
              bordered
              hover
              responsive
              class="w-100"
            >
              <template #cell(time)="row">
                {{ formatDate(row.value) }}
              </template>
            </b-table>
        </template>
          <div v-else-if="loaded">
            {{ chart.emptyMessage }}<b-icon icon="hand-thumbs-up" shift-h="8"></b-icon>
          </div>
          <SpinnerCmpt v-else></SpinnerCmpt>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import granularity from '@/xvisor/utilities/granularity';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';
import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import themeStyle from '@/xvisor/utilities/themeStyle';

export default {
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
  },
  components: {
    SpinnerCmpt,
  },
  data() {
    return {
      loaded: false,
      topZonesUrl: '/security/overview/topthreatzones.json',
      zoneThreatsSeriesUrl: '/security/overview/topthreatzonests.json',
      networkTopThreatServersUrl: '/security/overview/networkthreatservers.json',
      endpointTopThreatServersUrl: '/security/overview/endpointthreatservers.json',
      topVulPackagesUrl: '/security/overview/topvulpackages.json',
      recentPackagesUrl: '/security/overview/recentpackages.json',
      topZonesData: [],
      zoneThreatsSeriesData: [],
      networkTopThreatServersData: [],
      endpointTopThreatServersData: [],
      topVulPackagesData: [],
      recentPackagesData: [],
      chartColors: [
        colorPaletteShade.teal4,
        colorPaletteShade.orange5,
        colorPaletteShade.indigo4,
        colorPaletteShade.purple4,
        colorPaletteShade.green4,
      ],
    };
  },
  mounted() {
    this.httpGet();
  },
  watch: {
    timeRange() {
      this.httpGet();
    },
  },
  computed: {
    httpTimeRangeParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
        },
      };
    },
    httpTimeRangeAndStepParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          stepMinutes: granularity.granularity6(this.timeRange),
        },
      };
    },
    toggleColor() {
      return themeStyle.styleToggle(colorPalette.black, colorPalette.white);
    },
    baseChartOption() {
      return {
        title: {
          show: false,
        },
        tooltip: {
          trigger: 'item',
        },
        legend: {
          bottom: 0,
          textStyle: {
            color: this.toggleColor,
          },
          formatter: (name) => {
            const maxLen = 10;
            return name.length > maxLen ? `${name.substring(0, maxLen)}...` : name;
          },
        },
        color: this.chartColors,
      };
    },
    basePieOption() {
      return {
        ...this.baseChartOption,
        series: [
          {
            type: 'pie',
            radius: ['45%', '72%'],
            center: ['50%', '42%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: colorPaletteShade.gray3,
              borderWidth: 2,
            },
            label: {
              show: false,
              position: 'center',
            },
            emphasis: {
              label: {
                show: true,
                fontWeight: 'bold',
                fontSize: 12,
              },
            },
            labelLine: {
              show: false,
            },
          },
        ],
      };
    },
    baseVerticalBarOption() {
      return {
        ...this.baseChartOption,
        grid: {
          left: '5%',
          right: '10%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'time',
          boundaryGap: true,
          splitLine: {
            show: false,
          },
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            formatter: (value) => moment(value).format('MMM D h:mm A'),
            fontSize: 8,
            showMinLabel: false,
            showMaxLabel: false,
          },
        },
        yAxis: {
          type: 'value',
          name: '',
          nameLocation: 'center',
          nameGap: 35,
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            fontSize: 9,
          },
        },
      };
    },
    baseHorizontalBarOption() {
      return {
        ...this.baseChartOption,
        grid: {
          left: '10%',
          right: '5%',
          top: '5%',
          bottom: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          boundaryGap: true,
          splitLine: {
            show: false,
          },
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            fontSize: 8,
          },
        },
        yAxis: {
          type: 'category',
          name: 'Server Ip',
          nameLocation: 'center',
          inverse: true,
          nameGap: 80,
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            fontSize: 9,
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          trigger: 'item',
          formatter: (params) => `${params.seriesName}: ${params.value}`,
        },
      };
    },
    charts() {
      return [
        {
          key: 'top-threat-zones',
          title: 'Top Zones by Threats',
          emptyMessage: 'No zone has threats',
          type: 'chart',
          hasData: this.topZonesData && this.topZonesData.length > 0,
          option: {
            ...this.basePieOption,
            series: [
              {
                ...this.basePieOption.series[0],
                data: this.topZonesData,
              },
            ],
          },
        },
        {
          key: 'zone-threats-over-time',
          title: 'Threats by Zone Over Time',
          emptyMessage: 'No zone has threats',
          type: 'chart',
          hasData: this.zoneThreatsSeriesData && this.zoneThreatsSeriesData.length > 0,
          option: {
            ...this.baseVerticalBarOption,
            series: this.getFormattedVBarSeries(this.zoneThreatsSeriesData),
          },
        },
        {
          key: 'network-traffic',
          title: 'Top Threat Server IPs (from Network Traffic)',
          emptyMessage: 'No offending connections to threat servers',
          type: 'chart',
          hasData: this.networkTopThreatServersData && this.networkTopThreatServersData.length > 0,
          option: {
            ...this.baseHorizontalBarOption,
            yAxis: {
              ...this.baseHorizontalBarOption.yAxis,
              data: this.networkTopThreatServersData.map((d) => d.serverIp),
            },
            series: this.getFormattedHBarSeries(this.networkTopThreatServersData),
          },
        },
        {
          key: 'endpoint-traffic',
          title: 'Top Threat Server IPs (from Endpoint Traffic)',
          emptyMessage: 'No offending connections to threat servers',
          type: 'chart',
          hasData: this.endpointTopThreatServersData && this.endpointTopThreatServersData.length > 0,
          option: {
            ...this.baseHorizontalBarOption,
            yAxis: {
              ...this.baseHorizontalBarOption.yAxis,
              data: this.endpointTopThreatServersData.map((d) => d.serverIp),
            },
            series: this.getFormattedHBarSeries(this.endpointTopThreatServersData),
          },
        },
        {
          key: 'top-vul-packages',
          title: 'Top Installed Vulnerable Packages',
          emptyMessage: 'No vulnerable packages found',
          type: 'chart',
          hasData: this.topVulPackagesData && this.topVulPackagesData.length > 0,
          option: {
            ...this.basePieOption,
            series: [
              {
                ...this.basePieOption.series[0],
                data: this.topVulPackagesData,
              },
            ],
          },
        },
        {
          key: 'recent-packages',
          title: 'Recently Installed Packages',
          emptyMessage: 'No recent package installations',
          type: 'table',
          hasData: this.recentPackagesData && this.recentPackagesData.length > 0,
          fields: [
            { key: 'name', label: 'Name', sortable: true },
            { key: 'count', label: 'Count', sortable: true },
            { key: 'time', label: 'Last Installed On', sortable: true },
          ],
          items: this.recentPackagesData,
        },
      ];
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;
      axios
        .all([
          this.$http.get(this.topZonesUrl, this.httpTimeRangeParams),
          this.$http.get(this.zoneThreatsSeriesUrl, this.httpTimeRangeAndStepParams),
          this.$http.get(this.networkTopThreatServersUrl, this.httpTimeRangeParams),
          this.$http.get(this.endpointTopThreatServersUrl, this.httpTimeRangeParams),
          this.$http.get(this.topVulPackagesUrl, this.httpTimeRangeParams),
          this.$http.get(this.recentPackagesUrl, this.httpTimeRangeParams),
        ])
        .then(axios.spread((topZonesRes, zoneSeriesRes, networkRes, endpointRes, topVulPkgsRes, recentPkgsRes) => {
          this.topZonesData = topZonesRes.data;
          this.zoneThreatsSeriesData = zoneSeriesRes.data;
          this.networkTopThreatServersData = networkRes.data;
          this.endpointTopThreatServersData = endpointRes.data;
          this.topVulPackagesData = topVulPkgsRes.data;
          this.recentPackagesData = recentPkgsRes.data;
        }))
        .finally(() => { this.loaded = true; });
    },
    getFormattedVBarSeries(seriesData) {
      return seriesData.map((seriesItem, index) => ({
        name: seriesItem.name,
        type: 'bar',
        stack: 'bar',
        data: seriesItem.points.map((p) => [p.time, p.value]),
        itemStyle: {
          color: this.chartColors[index % this.chartColors.length],
        },
      }));
    },
    getFormattedHBarSeries(seriesData) {
      const allSources = Array.from(
        new Set(seriesData.flatMap((data) => data.sources.map((source) => source.ip))),
      );

      return allSources.map((ip, index) => ({
        name: ip,
        type: 'bar',
        stack: 'connections',
        data: seriesData.map(
          (data) => data.sources.find((source) => source.ip === ip)?.count || 0,
        ),
        itemStyle: {
          color: this.chartColors[index % this.chartColors.length],
        },
      }));
    },
    formatDate(dateStr) {
      return moment(dateStr).format(momentTimeFormat.dateTime);
    },
  },
};
</script>
<style scoped>
.header-card {
  width: 100%;
}
.header-card>.card-body {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}
.row-height {
  height: 42vh;
}
</style>
